import { Injectable } from '@angular/core';

declare var Genesys: any;

@Injectable({
  providedIn: 'root',
})
export class GenesysChatService {
  private bMessengerOpened = false;
  private genesysLoaded = false;
  private fallbackCallback: ((error?: string) => void) | null = null;
  private simulateFailure = false; // Set to true to test fallback, false for normal Genesys

  constructor() {}

  initializeGenesys(): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.genesysLoaded) {
        resolve(true);
        return;
      }

      const initTimeout = setTimeout(() => {
        this.genesysLoaded = false;
        resolve(false);
      }, 5000);

      try {
        const self = this;
        (function (g: any, e: any, n: any, es: any) {
          g['_genesysJs'] = e;
          g[e] =
            g[e] ||
            function () {
              (g[e].q = g[e].q || []).push(arguments);
            };
          g[e].t = Date.now();
          g[e].c = es;
          const ys = document.createElement('script');
          ys.async = true;
          ys.src = n;
          ys.charset = 'utf-8';
          ys.onload = () => {
            setTimeout(() => {
              try {
                if (typeof (window as any).Genesys === 'function') {
                  clearTimeout(initTimeout);
                  self.genesysLoaded = true;
                  self.setupGenesysEventSubscriptions();
                  resolve(true);
                } else {
                  clearTimeout(initTimeout);
                  resolve(false);
                }
              } catch (error) {
                clearTimeout(initTimeout);
                resolve(false);
              }
            }, 1000);
          };
          ys.onerror = () => {
            clearTimeout(initTimeout);
            resolve(false);
          };
          document.head.appendChild(ys);
        })(
          window,
          'Genesys',
          'https://apps.usw2.pure.cloud/genesys-bootstrap/genesys.min.js',
          {
            environment: 'prod-usw2',
            deploymentId: 'cbfba071-86d4-476d-b1f1-5984c51c719b',
          }
        );
      } catch (error) {
        clearTimeout(initTimeout);
        resolve(false);
      }
    });
  }

  private setupGenesysEventSubscriptions(): void {
    if (typeof (window as any).Genesys !== 'undefined') {
      const Genesys = (window as any).Genesys;

      Genesys('subscribe', 'Messenger.opened', () => {
        this.bMessengerOpened = true;
      });

      Genesys('subscribe', 'Messenger.closed', () => {
        this.bMessengerOpened = false;
      });
    }
  }

  setFallbackCallback(callback: (error?: string) => void): void {
    this.fallbackCallback = callback;
  }

  private messagingFallback(error?: string): void {
    if (this.fallbackCallback) {
      this.fallbackCallback(error);
    }
  }

  openMessenger(): void {
    if (
      typeof Genesys === 'undefined' ||
      !this.genesysLoaded ||
      this.simulateFailure
    ) {
      this.messagingFallback('Genesys not available or not loaded');
      return;
    }

    const messengerTimeout = setTimeout(() => {
      this.genesysLoaded = false;
      this.messagingFallback('Messenger open timed out');
    }, 3000);

    const chatLanguage = sessionStorage.getItem('languagepreference') || 'en';
    try {
      Genesys('subscribe', 'Messenger.ready', (e: any) => {
        Genesys('subscribe', 'Database.ready', () => {
          Genesys(
            'command',
            'Database.set',
            {
              messaging: {
                customAttributes: {
                  key: 'CHAT_ChatFlowName|msTechSupport;CHAT_SystemNeedHelp|mySedgwick - US',
                  language: chatLanguage.toLocaleUpperCase()
                },
              },
            },
            (data: any) => {
              clearTimeout(messengerTimeout);
              this.bMessengerOpened = true;
              Genesys('command', 'Messenger.open');
            },
            (error: any) => {
              clearTimeout(messengerTimeout);
              const message = error?.message ?? (error ? String(error) : 'Database set failed');
              this.messagingFallback(message);
            }
          );
        });
      });
    } catch (error) {
      clearTimeout(messengerTimeout);
      const message = error instanceof Error ? error.message : (error ? String(error) : 'Failed to open messenger');
      this.messagingFallback(message);
    }
  }

  closeMessenger(): void {
    if (typeof Genesys === 'undefined') {
      return;
    }

    this.bMessengerOpened = false;
    Genesys('command', 'Messenger.close');
  }

  toggleMessenger(): void {
    this.bMessengerOpened ? this.closeMessenger() : this.openMessenger();
  }

  isMessengerOpened(): boolean {
    return this.bMessengerOpened;
  }

  forceResetMessengerState(): void {
    this.bMessengerOpened = false;
  }

  isGenesysLoaded(): boolean {
    return (
      this.genesysLoaded &&
      typeof Genesys !== 'undefined' &&
      !this.simulateFailure
    );
  }

  simulateGenesysFailure(enable: boolean = true): void {
    this.simulateFailure = enable;
    if (!enable) {
      this.genesysLoaded = false;
    }
  }

  forceReset(): void {
    this.genesysLoaded = false;
    this.bMessengerOpened = false;
    this.simulateFailure = false;
  }
}

// JS Code received from the Chat team which is adapted into the service above
// //MESSENGER DEPLOYMENT
// (function (g, e, n, es, ys) {
//     g['_genesysJs'] = e;
//     g[e] = g[e] || function () {
//         (g[e].q = g[e].q || []).push(arguments)
//     };
//     g[e].t = 1 * new Date();
//     g[e].c = es;
//     ys = document.createElement('script'); ys.async = 1; ys.src = n; ys.charset = 'utf-8'; document.head.appendChild(ys);
//     })(window, 'Genesys', 'https://apps.usw2.pure.cloud/genesys-bootstrap/genesys.min.js', {
//     environment: 'prod-usw2',
//     deploymentId: 'cbfba071-86d4-476d-b1f1-5984c51c719b'
// });

// /*
//     Summary of code:

//     - Declares and initializes variables for a custom launcher button and Messenger open status.
//     - Adds a click event listener to the custom launcher button to toggle the Messenger.
//     - Defines a messagingFallback function to handle fallback logic if Messenger fails to open, displaying a specific dialog.
//     - Implements the openMessenger function, which:
//         - Waits for the Genesys Messenger and Database plugins to be ready.
//         - Sets a custom chat flow name attribute in the Genesys Database.
//         - If successful, marks the Messenger as open and opens the Messenger window.
//         - If any step fails, logs the error and invokes the fallback logic.
// */

// //VARIABLE DECLARATIONS
// // Custom launcher button. NOTE: This is not required if the button already exists/the openMessenger function is applied.
// var customLauncher = document.getElementById('custom-launcher');
// customLauncher.addEventListener("click", toggleMessenger);

// // Initial status of Messenger: closed
// let bMessengerOpened = false;

// //FUNCTIONS
// const messagingFallback = () => {
//     //PLACEHOLDER FOR FALLBACK LOGIC
//     console.log('Messaging fallback...');
// };

// /*
//     The openMessenger function attempts to open the Genesys Messenger window.
//     It first subscribes to the 'Messenger.ready' event to ensure the Messenger is initialized.
//     Once ready, it subscribes to the 'Database.ready' event to confirm the Genesys Database plugin is available.
//     When both are ready, it sets a custom attribute (CHAT_chatFlowName) in the Genesys Database to specify the chat flow.
//     If the attribute is set successfully, it marks the Messenger as open (bMessengerOpened = true) and opens the Messenger window.
//     If any step fails (such as the database set being rejected or an error occurring), it logs the error and calls the messagingFallback function to handle the failure.
// */
// const openMessenger = () => {
//     console.log('Opening messenger...');

//     try {
//         Genesys(
//             'subscribe',
//             'Messenger.ready',
//             (e) => {
//                 Genesys("subscribe", "Database.ready", function() {
//                     console.log("Database plugin is ready.");

//                     Genesys("command", "Database.set",
//                          { messaging: { customAttributes: {
//                             key: 'CHAT_ChatFlowName|msTechSupport;CHAT_SystemNeedHelp|mySedgwick - US' }

//                         }},

//                         function(data){
//                             /* fulfilled, returns data */
//                             console.log("Data set successful");
//                             bMessengerOpened = true;
//                             Genesys('command', 'Messenger.open');
//                             },
//                         function(error){
//                             console.log("DB Set rejected", error);
//                             messagingFallback();
//                         });
//                 });
//             }
//             );

//     } catch (error) {
//         console.log('Error subscribing to Messenger.ready:', error);
//         messagingFallback();
//     }

// };

// //The closeMessenger function closes the Messenger window and updates the bMessengerOpened status accordingly.
// const closeMessenger = () => {
//     console.log('Closing messenger...');
//     bMessengerOpened = false;
//     Genesys('command', 'Messenger.close');
// };

// //The toggleMessenger function toggles the Messenger window open and closed. The bMessengerOpened variable is set by the functions
// const toggleMessenger = () => {
//     //console.log("Clicked");
//     bMessengerOpened ? closeMessenger() : openMessenger();
// };

// //GENESYS EVENT SUBSCRIPTIONS

// //This ensures the toggle button works as expected
// Genesys("subscribe", "Messenger.opened", () => {
//     bMessengerOpened = true;
// });

// //This ensures the toggle button works as expected
// Genesys("subscribe", "Messenger.closed", function(){
//     bMessengerOpened = false;
// });
